import { useCallback, useEffect, useMemo, useState } from 'react'

import type { StartJourneyButtonBaseProps } from 'src/shared/components/ui/buttons/start-journey-button-base'
import StartJourneyButtonBase from 'src/shared/components/ui/buttons/start-journey-button-base'
import { useConsumerStartJourneyEvent } from 'src/shared/features/consumers/hooks/message-form/use-consumer-start-journey-event'
import { useUserByUsernameQuery } from 'src/shared/features/users/api/accounts/use-user-by-username'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useIsAppleDevice, useIsAndroid } from 'src/shared/hooks/use-device-detect'
import { useEventStore } from 'src/shared/store/event-store'

import type { StartJourneyButtonTrackingEvent } from '../../schemas/start-journey-interfaces'
import StartJourneyPopup from '../start-journey-popup'

// Edit this list to hide SMS for specific pages.
const hideSmsPages = ['affordable', 'approved', 'findAnAgent']

const LYNK_TWILIO_PHONE_NUMBER = '+15165503566'

interface StartJourneyButtonProps extends Omit<StartJourneyButtonBaseProps, 'onClick'> {
  /** Optional tracking data that will be sent to backend and Google Ads. */
  trackingEvent?: StartJourneyButtonTrackingEvent

  /** The username of the user who is creating the consumer.
   * Set this to link to a professional's start journey button. */
  username?: string
}

/**
 * Start journey button for consumers.
 * On mobile this will open a SMS to Lynk's phone number.
 * On desktop this will open the start journey popup where the user can enter their phone number.
 */
export default function StartJourneyButton({ trackingEvent, username, ...props }: StartJourneyButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const sendEvent = useConsumerStartJourneyEvent()
  const { setFlowId, clearFlowId } = useEventStore()
  const { isMobile } = useBreakpoint()
  const isIOS = useIsAppleDevice()
  const isAndroid = useIsAndroid()

  const { data: user } = useUserByUsernameQuery(username)

  const agentData = useMemo(() => {
    if (!user) {
      return null
    }

    const twilioPhoneNumber = user.assignedTwilioPhoneNumber
    if (!twilioPhoneNumber) {
      return null
    }

    return {
      firstName: user.firstName,
      userId: user.id,
      twilioPhoneNumber,
    }
  }, [user])

  // Don't show SMS for these landing pages so we can track the conversion rate.
  const dontShowSms = hideSmsPages.includes(trackingEvent?.pageName || '')

  useEffect(() => {
    if (!isOpen) {
      clearFlowId()
    }
  }, [isOpen, clearFlowId])

  const onClick = useCallback(() => {
    if (isMobile && (isIOS || isAndroid) && !dontShowSms) {
      // General Lynk SMS number and message.
      let smsNumber = LYNK_TWILIO_PHONE_NUMBER
      let smsMessage = "Hi Lynk! I'm starting my real estate journey and need someone to help."

      // Agent specific SMS number and message.
      if (agentData) {
        smsNumber = agentData.twilioPhoneNumber
        smsMessage = `Hi Lynk! I'm starting my real estate journey and need help from ${agentData.firstName}.`
      }

      // iOS uses '&' separator, Android uses '?' separator
      const separator = isIOS ? '&' : '?'
      const smsUrl = `sms:${smsNumber}${separator}body=${encodeURIComponent(smsMessage)}`
      // Send tracking event if provided before redirecting to SMS.
      if (trackingEvent) {
        sendEvent({ pageName: trackingEvent.pageName, action: 'open_sms', tag: trackingEvent.tag }).finally(() => {
          window.location.href = smsUrl
        })
      } else {
        window.location.href = smsUrl
      }
    } else {
      if (trackingEvent) {
        const flowId = setFlowId()
        sendEvent({ pageName: trackingEvent.pageName, action: 'phone_number', flowId, tag: trackingEvent.tag })
      }
      setIsOpen(true)
    }
  }, [isMobile, isIOS, isAndroid, setIsOpen, sendEvent, trackingEvent, setFlowId, dontShowSms, agentData])

  return (
    <>
      <StartJourneyButtonBase {...props} onClick={onClick} />
      <StartJourneyPopup
        isOpen={isOpen}
        setOpen={setIsOpen}
        trackingEvent={trackingEvent}
        createdByUserId={agentData?.userId}
      />
    </>
  )
}
